import {
  AlertTriangleIcon,
  BrainCircuitIcon,
  CalendarIcon,
  ClockIcon,
  ImageIcon,
  MicIcon,
  SlidersHorizontal,
  TextIcon,
  VideoIcon,
  WrapTextIcon,
  XCircleIcon,
} from "lucide-react";

import { formatContextLength, formatPrice } from "./utils";

import { ProviderIcon } from "@/components/ProviderWithLogo/ProviderIcon";
import { ModelMetadataEntry } from "@/queries/models/useModelMetadata";

const MODALITY_ICONS: Record<string, React.ReactNode> = {
  text: <TextIcon className="h-3 w-3" />,
  image: <ImageIcon className="h-3 w-3" />,
  audio: <MicIcon className="h-3 w-3" />,
  video: <VideoIcon className="h-3 w-3" />,
  file: <WrapTextIcon className="h-3 w-3" />,
};

const InfoRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex items-start justify-between gap-4 py-1.5">
    <span className="shrink-0 text-xs font-medium text-gray-400">{label}</span>
    <span className="text-right text-xs text-gray-800">{children}</span>
  </div>
);

interface ModelDetailPanelProps {
  metadata: ModelMetadataEntry;
  modelName: string;
  provider?: string;
}

const LATENCY_STYLES: Record<string, { bg: string; text: string }> = {
  fast: { bg: "bg-green-50", text: "text-green-700" },
  moderate: { bg: "bg-yellow-50", text: "text-yellow-700" },
  slow: { bg: "bg-red-50", text: "text-red-700" },
};

export const ModelDetailPanel = ({ metadata, modelName, provider }: ModelDetailPanelProps) => {
  const ctx = formatContextLength(metadata.context_length);
  const inputPrice = formatPrice(metadata.price_per_1m_tokens_input);
  const outputPrice = formatPrice(metadata.price_per_1m_tokens_output);
  const maxOutput = formatContextLength(metadata.max_completion_tokens);

  const hasReasoning = metadata.supported_parameters.includes("reasoning");
  const hasReasoningEffort = metadata.supported_parameters.includes("reasoning_effort");

  const inputModalities = metadata.input_modalities;
  const outputModalities = metadata.output_modalities;

  const { latency } = metadata;
  const latencyStyle = latency ? (LATENCY_STYLES[latency] ?? LATENCY_STYLES.moderate) : null;

  return (
    <div className="w-72">
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3">
        {provider ? (
          <ProviderIcon provider={provider} />
        ) : (
          <SlidersHorizontal className="h-3.5 w-3.5 shrink-0 text-gray-400" />
        )}
        <span className="min-w-0 flex-1 truncate text-xs font-semibold text-gray-800">
          {metadata.name ?? modelName}
        </span>
      </div>

      <div className="px-4 pb-3 pt-2">
        {/* Description */}
        {metadata.description && (
          <p className="mb-3 text-[11px] leading-relaxed text-gray-500">{metadata.description}</p>
        )}

        {/* Deprecation / Retirement warnings */}
        {metadata.retirement_date && (
          <div className="mb-2 flex items-center gap-1.5 rounded border border-red-200 bg-red-50 px-2.5 py-1.5">
            <XCircleIcon className="h-3.5 w-3.5 shrink-0 text-red-500" />
            <span className="text-[11px] font-medium text-red-700">
              {new Date(metadata.retirement_date) < new Date() ? "Retired" : "Retires"}{" "}
              {metadata.retirement_date}
            </span>
          </div>
        )}
        {metadata.deprecation_date && !metadata.retirement_date && (
          <div className="mb-2 flex items-center gap-1.5 rounded border border-amber-200 bg-amber-50 px-2.5 py-1.5">
            <AlertTriangleIcon className="h-3.5 w-3.5 shrink-0 text-amber-500" />
            <span className="text-[11px] font-medium text-amber-700">
              {new Date(metadata.deprecation_date) < new Date() ? "Deprecated" : "Deprecates"}{" "}
              {metadata.deprecation_date}
            </span>
          </div>
        )}

        {/* Info rows */}
        <div className="divide-y divide-gray-100">
          {ctx && (
            <InfoRow label="Context">{metadata.context_length?.toLocaleString()} tokens</InfoRow>
          )}

          {maxOutput && (
            <InfoRow label="Max Output">
              {metadata.max_completion_tokens?.toLocaleString()} tokens
            </InfoRow>
          )}

          {(inputPrice ?? outputPrice) && (
            <InfoRow label="Pricing / 1M">
              {inputPrice ?? "N/A"} in / {outputPrice ?? "N/A"} out
            </InfoRow>
          )}

          {latency && latencyStyle && (
            <InfoRow label="Latency">
              <span className="flex items-center gap-1">
                <ClockIcon className="h-3 w-3 text-gray-400" />
                <span
                  className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${latencyStyle.bg} ${latencyStyle.text}`}
                >
                  {latency.charAt(0).toUpperCase() + latency.slice(1)}
                </span>
              </span>
            </InfoRow>
          )}

          {(hasReasoning || hasReasoningEffort) && (
            <InfoRow label="Reasoning">
              <span className="flex items-center gap-1">
                <BrainCircuitIcon className="h-3 w-3 text-gray-400" />
                Supported{hasReasoningEffort ? " (effort)" : ""}
              </span>
            </InfoRow>
          )}

          {inputModalities.length > 0 && (
            <InfoRow label="Input">
              <span className="flex flex-wrap justify-end gap-1">
                {inputModalities.map(m => (
                  <span
                    key={m}
                    className="inline-flex items-center gap-0.5 rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] text-gray-600"
                  >
                    {MODALITY_ICONS[m] ?? null}
                    {m}
                  </span>
                ))}
              </span>
            </InfoRow>
          )}

          {outputModalities.length > 0 && (
            <InfoRow label="Output">
              <span className="flex flex-wrap justify-end gap-1">
                {outputModalities.map(m => (
                  <span
                    key={m}
                    className="inline-flex items-center gap-0.5 rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] text-gray-600"
                  >
                    {MODALITY_ICONS[m] ?? null}
                    {m}
                  </span>
                ))}
              </span>
            </InfoRow>
          )}

          {metadata.knowledge_cutoff && (
            <InfoRow label="Knowledge">
              <span className="flex items-center gap-1">
                <CalendarIcon className="h-3 w-3 text-gray-400" />
                {metadata.knowledge_cutoff}
              </span>
            </InfoRow>
          )}
        </div>
      </div>
    </div>
  );
};
